import { Quill } from 'react-quill';

import {
  LetterSpacingStyle,
  LetterSpacingWhitelist,
} from './attributors/letterSpacing';
import { LineHeightStyle } from './attributors/lineHeight';
import {
  EMPTY_QUILL_DEFAULT_WRAPPERS,
  QUILL_BLOCK_TAGS,
} from './constants/quillBlockTags';
import { QuillToolbarDropDown } from './DynamicQuillTools';
import { IRichTextEditorDefaultStyleValues } from './RichTextWidget.interface';

export const constructSpecialCharSelect = (
  quill: any,
  specialChars: string[] = [],
  customHandler?: (char: string) => void,
  isDisabled?: boolean,
) => {
  const defaultChars = ['©', '®', '†', '™', '¶', '§'];

  const dropDownItems = constructSelectOptions(
    specialChars.length ? specialChars : defaultChars,
  );

  createCustomSelect(
    'Special chars',
    quill,
    dropDownItems,
    (label: string, value: string, quill: any) => {
      if (customHandler) {
        customHandler(value);
      } else {
        const { index, length } = quill.editor.selection.savedRange;
        quill.editor.deleteText(index, length);
        // Insert with superscript formatting if it's currently active
        const currentFormat = quill.editor.getFormat();
        if (currentFormat.script === 'super') {
          quill.editor.insertText(index, value, { script: 'super' }, 'user');
        } else {
          quill.editor.insertText(index, value, 'user');
        }
        quill.editor.setSelection(index + value.length);
      }
    },
    isDisabled,
  );
};

export const constructFontSelect = (quill: any, fontFamily: string[] = []) => {
  const defaultFonts = ['WeblySleekUI'];

  const Font = Quill.import('formats/font');
  Font.whitelist = fontFamily.length > 0 ? fontFamily : defaultFonts;
  Quill.register(Font, true);

  const dropDownItems = constructSelectOptions(Font.whitelist);

  createCustomSelect(
    'Font',
    quill,
    dropDownItems,
    (label: string, value: unknown, quill: any) =>
      setEditorPropertyValue('font', value, quill),
  );
};

export const constructSizeSelect = (
  quill: any,
  size: Array<string | number> = [],
  savedSelectionRef?: React.MutableRefObject<any>,
  isDisabled?: boolean,
) => {
  const Size = Quill.import('attributors/style/size');
  const defaultSizeOptions = [
    '10px',
    '12px',
    '14px',
    '16px',
    '18px',
    '20px',
    '24px',
    '32px',
    '48px',
  ];
  Size.whitelist = size.length > 0 ? size : defaultSizeOptions;
  Quill.register(Size, true);

  const dropDownItems = constructSelectOptions(Size.whitelist);

  createCustomSelect(
    'Size',
    quill,
    dropDownItems,
    (label: string, value: unknown, quill: any) => {
      if (savedSelectionRef?.current) {
        // Use saved selection to apply size to previously selected text
        const { index, length } = savedSelectionRef.current;
        quill.editor.formatText(index, length, 'size', value, 'user');
        // Restore selection after formatting
        quill.editor.setSelection(index, length);
      } else {
        setEditorPropertyValue('size', value, quill);
      }
    },
    isDisabled,
  );
};

export const constructLetterSpacingSelect = (
  quill: any,
  options: Array<string | number> = [],
) => {
  const customOptions = options.length > 0 ? options : LetterSpacingWhitelist;
  LetterSpacingStyle.whitelist = customOptions;
  Quill.register(LetterSpacingStyle, true);

  const dropDownItems = constructSelectOptions(LetterSpacingStyle.whitelist);

  createCustomSelect(
    'Letter spacing',
    quill,
    dropDownItems,
    (label: string, value: unknown, quill: any) =>
      setEditorPropertyValue('letter-spacing', value, quill),
  );
};

export const constructLineHeightSelect = (
  quill: any,
  options: Array<string | number> = [],
) => {
  const customOptions =
    options.length > 0 ? options : LineHeightStyle.whitelist;
  LineHeightStyle.whitelist = customOptions;
  Quill.register(LineHeightStyle, true);

  const dropDownItems = constructSelectOptions(LineHeightStyle.whitelist);

  createCustomSelect(
    'Line height',
    quill,
    dropDownItems,
    (label: string, value: unknown, quill: any) =>
      setEditorPropertyValue('line-height', value, quill),
  );
};

const constructSelectOptions = (data: any) => {
  return data.reduce((acc: { [key: string]: string }, curr: string) => {
    acc[curr] = curr;

    return acc;
  }, {});
};

const setEditorPropertyValue = (label: string, value: unknown, quill: any) => {
  const { index, length } = quill.editor.selection.savedRange;
  quill.editor.formatText(index, length, label, value, 'user');
  quill.editor.format(label, value, 'user');
};

const createCustomSelect = (
  label: string,
  quill: any,
  options: { [key: string]: string },
  onChangeHandler: (label: string, value: any, quill: any) => void,
  isDisabled?: boolean,
) => {
  const toolbar = quill.editor.getModule('toolbar');
  const toolbarContainer = toolbar.container;
  const existingSelect = toolbarContainer.querySelector(
    `[data-label^="${label}"]`,
  );
  if (existingSelect) {
    // Update existing dropdown state
    if (isDisabled !== undefined) {
      if (isDisabled) {
        existingSelect.style.opacity = '0.5';
        existingSelect.style.pointerEvents = 'none';
        existingSelect.setAttribute('disabled', 'true');
      } else {
        existingSelect.style.opacity = '1';
        existingSelect.style.pointerEvents = 'auto';
        existingSelect.removeAttribute('disabled');
      }
    }

    return; // Already exists, just update state
  }

  const customDropDown = new QuillToolbarDropDown({
    label,
    rememberSelection: true,
  });
  customDropDown.setItems(options);
  customDropDown.onSelect = onChangeHandler;
  customDropDown.attach(quill);

  // Apply initial disabled state if specified
  if (isDisabled) {
    const newDropdown = toolbarContainer.querySelector(
      `[data-label^="${label}"]`,
    );
    if (newDropdown) {
      newDropdown.style.opacity = '0.5';
      newDropdown.style.pointerEvents = 'none';
      newDropdown.setAttribute('disabled', 'true');
    }
  }
};

export const isEmptyQuillContent = (value: string): boolean => {
  return EMPTY_QUILL_DEFAULT_WRAPPERS.includes(
    value as (typeof EMPTY_QUILL_DEFAULT_WRAPPERS)[number],
  );
};

/**
 * Injects default styles (fontFamily, color) into an HTML string for all
 * elements that don't already have those properties set. Used to pre-apply
 * theme base styles to pre-filled rich text values before loading into Quill.
 *
 * Pre-normalises via normalizeInitialQuillValue so the result is already in
 * Quill's expected <span>-based format. This prevents the flicker caused by
 * Quill's own async normalisation pass converting <p> → <span> on first mount.
 */
export const injectDefaultFontIntoHtml = (
  html: string,
  styles: { fontFamily?: string; color?: string },
): string => {
  const { fontFamily, color } = styles;
  if (!html || (!fontFamily && !color)) return html;

  // Normalize to Quill-compatible structure first so styles land on <span>
  // elements rather than block-level tags that Quill won't parse as text formats.
  const normalized = normalizeInitialQuillValue(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(normalized, 'text/html');

  // Block-level tags used as line containers by Quill. Inline styles placed
  // directly on these are ignored by Quill's inline attributors — styles must
  // live on an inner <span> so Quill reads them as character-level formats.
  const blockTags = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

  const applyStyles = (el: HTMLElement) => {
    if (blockTags.has(el.tagName)) {
      // A block element whose only element children are <br> tags (or has no
      // element children at all) contains plain text / line-breaks only.
      // Wrap the content in a <span> so Quill reads font/color as inline
      // character-level formats rather than ignoring them on the block element.
      const hasOnlyBrOrNoChildren =
        el.children.length === 0 ||
        Array.from(el.children).every(
          (child) => (child as HTMLElement).tagName === 'BR',
        );

      if (hasOnlyBrOrNoChildren && el.innerHTML.trim()) {
        const span = doc.createElement('span');
        if (fontFamily) span.style.fontFamily = fontFamily;
        if (color) span.style.color = color;
        span.innerHTML = el.innerHTML;
        el.innerHTML = '';
        el.appendChild(span);
      } else {
        Array.from(el.children).forEach((child) =>
          applyStyles(child as HTMLElement),
        );
      }

      return;
    }

    // Inline element (span, strong, em, …)
    if (el.children.length === 0) {
      if (fontFamily) {
        el.style.fontFamily = fontFamily;
      }
      if (color && !el.style.color) {
        el.style.color = color;
      }

      return;
    }
    Array.from(el.children).forEach((child) =>
      applyStyles(child as HTMLElement),
    );
  };

  Array.from(doc.body.children).forEach((child) =>
    applyStyles(child as HTMLElement),
  );

  return doc.body.innerHTML;
};

export const applyDefaultStyleValues = (
  quill: any,
  defaultStyleValues?: IRichTextEditorDefaultStyleValues,
) => {
  const editor = quill.getEditor();
  if (editor && defaultStyleValues) {
    Object.entries(defaultStyleValues).forEach(([key, value]) => {
      if (value) {
        switch (key) {
          case 'fontFamily':
            key = 'font';
            break;
          case 'fontSize':
            key = 'size';
            break;
          case 'letterSpacing':
            key = 'letter-spacing';
            break;
          case 'lineHeight':
            key = 'line-height';
            break;
          case 'backgroundColor':
            key = 'background-color';
            break;
          default:
            break;
        }
        setEditorPropertyValue(key, value, quill);
      }
    });
  }
};

export function extractStylesFromHtml(
  htmlString: string | undefined,
): IRichTextEditorDefaultStyleValues {
  if (htmlString === undefined) return {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const firstElement = Array.from(doc.body.childNodes).find(
    (node): node is HTMLElement => node.nodeType === 1,
  );

  if (!firstElement) return {};

  // Verify if the element is a <div class="pre">, a React Quill Standard Wrapper, and select its first child
  let targetElement: HTMLElement | undefined;
  if (
    firstElement.tagName === 'DIV' &&
    firstElement.classList.contains('pre')
  ) {
    targetElement = Array.from(firstElement.childNodes).find(
      (node): node is HTMLElement => node.nodeType === 1,
    );
  } else {
    targetElement = firstElement;
  }

  if (!targetElement) return {};

  const tagName = targetElement.tagName.toLocaleLowerCase();
  if (QUILL_BLOCK_TAGS.has(tagName)) {
    const h1Child = Array.from(targetElement.childNodes).find(
      (node): node is HTMLElement => node.nodeType === 1,
    );
    if (h1Child) targetElement = h1Child;
  }

  const targetStyles: IRichTextEditorDefaultStyleValues = {
    fontFamily: targetElement.style.fontFamily,
    fontSize: targetElement.style.fontSize,
    letterSpacing: targetElement.style.letterSpacing,
    lineHeight: targetElement.style.lineHeight,
    color: targetElement.style.color,
    backgroundColor: targetElement.style.backgroundColor,
  };

  // The object only will be populated with values if it has a valid value
  for (const key in targetStyles) {
    const typedKey = key as keyof IRichTextEditorDefaultStyleValues;
    if (targetStyles[typedKey] === '' || targetStyles[typedKey] === undefined) {
      delete targetStyles[typedKey];
    }
  }

  return targetStyles;
}

export function normalizeInitialQuillValue(rawHtml: string): string {
  // Quill tags that apply its styles
  const quillTagsSet = new Set([
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'code',
    'a',
    'span',
    'sup',
  ]);

  const fallbackTag = 'span'; // Generic quill html tag

  const container = document.createElement('div');
  container.innerHTML = rawHtml.trim();

  let firstEl = container.firstElementChild;

  // If not wrapped in <div class="pre">, wrap it
  if (
    !(firstEl && firstEl.tagName === 'DIV' && firstEl.classList.contains('pre'))
  ) {
    const wrapper = document.createElement('div');
    wrapper.className = 'pre';
    wrapper.innerHTML = container.innerHTML;
    container.innerHTML = '';
    container.appendChild(wrapper);
    firstEl = wrapper;
  }

  // Get the first child inside the .pre div
  let current = firstEl.firstElementChild;

  // If the element has inline styles and is not an allowed tag, replace it with <span>
  if (current && current.hasAttribute('style')) {
    const tag = current.tagName.toLowerCase();

    if (!quillTagsSet.has(tag)) {
      const replacedHTML = current.outerHTML
        .replace(new RegExp(`^<${tag}\\b`, 'i'), `<${fallbackTag}`)
        .replace(new RegExp(`</${tag}>$`, 'i'), `</${fallbackTag}>`);
      current.outerHTML = replacedHTML;
    }
  }

  return container.innerHTML;
}
